import { KiteHolding, KitePosition, KiteUserProfile } from './kiteTypes';

// Environment check (removed sensitive logging)
if (!import.meta.env.VITE_KITE_API_KEY) {
  console.warn('KITE_API_KEY not configured');
}

const KITE_API_KEY = import.meta.env.VITE_KITE_API_KEY;

// const KITE_API_KEY = '5u84n8qds3ezt17u'
// Kite Connect API configuration
const KITE_LOGIN_URL = 'https://kite.zerodha.com/connect/login';
const KITE_REDIRECT_URL = typeof window !== 'undefined' 
  ? `${window.location.origin}/kite-callback` 
  : 'https://www.wealthmanager.online/kite-callback';

// Authentication state management
interface AuthState {
  accessToken: string | null;
  userData: any | null;
  isAuthenticated: boolean;
}

let authState: AuthState = {
  accessToken: null,
  userData: null,
  isAuthenticated: false
};

// Initialize auth state from localStorage
export const initializeAuthState = () => {
  if (typeof window === 'undefined') return;
  
  const storedToken = localStorage.getItem('kite_access_token');
  const storedUserData = localStorage.getItem('kite_user_data');
  
  if (storedToken && storedUserData) {
    authState = {
      accessToken: storedToken,
      userData: JSON.parse(storedUserData),
      isAuthenticated: true
    };
  }
};

// Get current authentication state
export const getAuthState = (): AuthState => {
  return { ...authState };
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return authState.isAuthenticated && !!authState.accessToken;
};

// Get Kite login URL
export const getKiteLoginUrl = (): string => {
  if (!KITE_API_KEY) {
    throw new Error('Kite API key not configured');
  }
  
  console.log('Generating Kite login URL');
  
  const params = new URLSearchParams({
    api_key: KITE_API_KEY,
    redirect_url: KITE_REDIRECT_URL,
    state: 'login_' + Date.now() // Add state for security
  });
  
  const loginUrl = `${KITE_LOGIN_URL}?${params.toString()}`;
  console.log('Generated login URL with parameters');
  
  return loginUrl;
};

// Extract request token from URL callback
export const extractRequestToken = (url?: string): string | null => {
  if (typeof window === 'undefined') return null;
  
  const extractStartTime = Date.now();
  const urlToCheck = url || window.location.href;
  
  console.log('=== TOKEN EXTRACTION STARTED ===');
  console.log('Extract start time:', new Date().toISOString());
  
  const urlParams = new URLSearchParams(new URL(urlToCheck).search);
  console.log('URL search params found:', urlParams.size);
  
  // Check for error response
  if (urlParams.has('error')) {
    const errorType = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    console.error('Kite authentication error:', errorType, errorDescription);
    
    // Store error for UI display
    localStorage.setItem('kite_auth_error', errorDescription || errorType || 'Unknown error');
    return null;
  }
  
  // Extract request token
  const requestToken = urlParams.get('request_token');
  const extractEndTime = Date.now();
  const extractDuration = extractEndTime - extractStartTime;
  
  if (requestToken) {
    // Store the token extraction timestamp for lifecycle tracking
    const tokenData = {
      token: requestToken,
      extractedAt: extractEndTime,
      extractedAtISO: new Date().toISOString(),
      pageUrl: urlToCheck
    };
    localStorage.setItem('kite_token_lifecycle', JSON.stringify(tokenData));
    
    return requestToken;
  }
  return null;
};

// Exchange request token for access token
export const exchangeToken = async (requestToken: string): Promise<any> => {
  const exchangeStartTime = Date.now();
  
  try {
    
    // Starting token exchange process
    
    // Check token lifecycle data for expiration validation
    const lifecycleData = localStorage.getItem('kite_token_lifecycle');
    if (lifecycleData) {
      try {
        const parsed = JSON.parse(lifecycleData);
        const tokenAge = exchangeStartTime - parsed.extractedAt;
        
        if (tokenAge > 120000) { // 2 minutes
          console.warn('Token may be expired due to age');
        }
      } catch (parseError) {
        // Ignore parse errors for lifecycle data
      }
    }
    
    // Validate request token before proceeding
    if (!requestToken || requestToken.length < 10) {
      const error = `Invalid request token: ${requestToken?.length || 0} characters (minimum 10 required)`;
      console.error(error);
      throw new Error(error);
    }
    
    // In development mode, use simulated response
    if (import.meta.env.DEV && localStorage.getItem('kite_use_simulation') === 'true') {
      console.log('Using simulated token exchange');
      
      const simulatedResponse = {
        success: true,
        accessToken: 'simulated_access_token_' + Date.now(),
        userId: 'SIMULATED_USER',
        userName: 'John Doe',
        userShortname: 'John',
        email: 'john@example.com',
        loginTime: new Date().toISOString()
      };
      
      // Store tokens
      localStorage.setItem('kite_access_token', simulatedResponse.accessToken);
      localStorage.setItem('kite_user_data', JSON.stringify(simulatedResponse));
      
      // Update auth state
      authState = {
        accessToken: simulatedResponse.accessToken,
        userData: simulatedResponse,
        isAuthenticated: true
      };
      
      return simulatedResponse;
    }
    
    // Real API call through Supabase Edge Function
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('User not authenticated with Supabase');
    }
    
    const edgeFunctionStartTime = Date.now();
    const timeSinceExchangeStart = edgeFunctionStartTime - exchangeStartTime;
    
    // Calling Edge Function for token exchange
    
    // Add timeout handling for Edge Function calls
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out after 30 seconds')), 30000)
    );
    
    const edgeFunctionPromise = supabase.functions.invoke('kite-connect-api', {
      body: {
        action: 'exchangeToken',
        requestToken: requestToken,
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    const { data, error } = await Promise.race([edgeFunctionPromise, timeoutPromise]) as any;
    
    const edgeFunctionEndTime = Date.now();
    const edgeFunctionDuration = edgeFunctionEndTime - edgeFunctionStartTime;
    const totalExchangeTime = edgeFunctionEndTime - exchangeStartTime;
    
    // Edge Function response processing
    
    if (error) {
      console.error('Edge Function error:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to connect to Zerodha service';
      
      if (error.message) {
        if (error.message.includes('fetch')) {
          errorMessage = 'Failed to send request to Edge Function. Please check your internet connection and try again.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Network configuration error. Please contact support.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (error.message.includes('unauthorized') || error.message.includes('401')) {
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Zerodha service is temporarily unavailable. Please try again in a few minutes.';
        } else {
          errorMessage = `Connection failed: ${error.message}`;
        }
      }
      
      throw new Error(errorMessage);
    }
    
    // Process Edge Function response
    
    if (data.success) {
      // Store tokens
      localStorage.setItem('kite_access_token', data.accessToken);
      localStorage.setItem('kite_user_data', JSON.stringify(data));
      
      // Update auth state
      authState = {
        accessToken: data.accessToken,
        userData: data,
        isAuthenticated: true
      };
      
      const completionTime = Date.now();
      const totalTime = completionTime - exchangeStartTime;
      
      // Update lifecycle tracking
      if (lifecycleData) {
        try {
          const parsed = JSON.parse(lifecycleData);
          const updatedLifecycle = {
            ...parsed,
            exchangeCompletedAt: completionTime,
            exchangeCompletedAtISO: new Date().toISOString(),
            totalLifecycleTime: completionTime - parsed.extractedAt,
            frontendExchangeTime: totalTime
          };
          localStorage.setItem('kite_token_lifecycle', JSON.stringify(updatedLifecycle));
        } catch (parseError) {
          // Ignore parse errors for lifecycle data
        }
      }
      return data;
    } else {
      throw new Error(data.error || 'Token exchange failed');
    }
  } catch (error) {
    const errorTime = Date.now();
    const totalTime = errorTime - exchangeStartTime;
    
    console.error('Token exchange failed:', error.message || 'Unknown error');
    
    // Store error for UI display
    localStorage.setItem('kite_auth_error', error.message || 'Token exchange failed');
    
    return { success: false, error: error.message || 'Token exchange failed' };
  }
};

// Fetch user profile
export const fetchUserProfile = async (accessToken?: string): Promise<any> => {
  try {
    const token = accessToken || authState.accessToken;
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    // Simulated response for development
    if (token.includes('simulated')) {
      console.log('Using simulated user profile');
      
      return {
        success: true,
        profile: {
          user_id: 'SIMULATED_USER',
          user_name: 'John Doe',
          user_shortname: 'John',
          email: 'john@example.com',
          user_type: 'individual',
          broker: 'ZERODHA',
          exchanges: ['NSE', 'BSE', 'MCX'],
          products: ['CNC', 'MIS', 'NRML'],
          order_types: ['MARKET', 'LIMIT', 'SL', 'SL-M'],
          avatar_url: null
        }
      };
    }
    
    // Real API call
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase.functions.invoke('kite-connect-api', {
      body: {
        action: 'fetchProfile',
        accessToken: token
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { success: false, error: error.message || 'Failed to fetch profile' };
  }
};

// Fetch holdings
export const fetchHoldings = async (accessToken?: string): Promise<any> => {
  try {
    const token = accessToken || authState.accessToken;
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    // Simulated response for development
    if (token.includes('simulated')) {
      console.log('Using simulated holdings data');
      
      return {
        success: true,
        holdings: [
          {
            tradingsymbol: 'RELIANCE',
            exchange: 'NSE',
            instrument_token: 738561,
            isin: 'INE002A01018',
            product: 'CNC',
            price: 2540.80,
            quantity: 10,
            used_quantity: 0,
            t1_quantity: 0,
            realised_quantity: 10,
            authorised_quantity: 10,
            authorised_date: '2024-01-15 00:00:00',
            opening_quantity: 10,
            short_quantity: 0,
            collateral_quantity: 0,
            collateral_type: null,
            discrepancy: false,
            average_price: 2450.75,
            last_price: 2540.80,
            close_price: 2530.25,
            pnl: 900.50,
            day_change: 10.55,
            day_change_percentage: 0.42
          },
          {
            tradingsymbol: 'HDFCBANK',
            exchange: 'NSE',
            instrument_token: 341249,
            isin: 'INE040A01034',
            product: 'CNC',
            price: 1680.75,
            quantity: 20,
            used_quantity: 0,
            t1_quantity: 0,
            realised_quantity: 20,
            authorised_quantity: 20,
            authorised_date: '2024-01-10 00:00:00',
            opening_quantity: 20,
            short_quantity: 0,
            collateral_quantity: 0,
            collateral_type: null,
            discrepancy: false,
            average_price: 1650.30,
            last_price: 1680.75,
            close_price: 1670.50,
            pnl: 608.50,
            day_change: 10.25,
            day_change_percentage: 0.61
          }
        ]
      };
    }
    
    // Real API call
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase.functions.invoke('kite-connect-api', {
      body: {
        action: 'fetchHoldings',
        accessToken: token
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching holdings:', error);
    return { success: false, error: error.message || 'Failed to fetch holdings' };
  }
};

// Fetch positions
export const fetchPositions = async (accessToken?: string): Promise<any> => {
  try {
    const token = accessToken || authState.accessToken;
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    // Simulated response for development
    if (token.includes('simulated')) {
      console.log('Using simulated positions data');
      
      return {
        success: true,
        positions: {
          net: [
            {
              tradingsymbol: 'RELIANCE',
              exchange: 'NSE',
              instrument_token: 738561,
              product: 'CNC',
              quantity: 10,
              overnight_quantity: 10,
              multiplier: 1,
              average_price: 2450.75,
              close_price: 2530.25,
              last_price: 2540.80,
              value: 25408.00,
              pnl: 900.50,
              m2m: 105.50,
              unrealised: 900.50,
              realised: 0,
              buy_quantity: 10,
              buy_price: 2450.75,
              buy_value: 24507.50,
              buy_m2m: 24507.50,
              day_buy_quantity: 0,
              day_buy_price: 0,
              day_buy_value: 0,
              sell_quantity: 0,
              sell_price: 0,
              sell_value: 0,
              sell_m2m: 0,
              day_sell_quantity: 0,
              day_sell_price: 0,
              day_sell_value: 0
            }
          ],
          day: []
        }
      };
    }
    
    // Real API call
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase.functions.invoke('kite-connect-api', {
      body: {
        action: 'fetchPositions',
        accessToken: token
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching positions:', error);
    return { success: false, error: error.message || 'Failed to fetch positions' };
  }
};

// Fetch complete portfolio (holdings + positions)
export const fetchPortfolio = async (accessToken?: string): Promise<any> => {
  try {
    const token = accessToken || authState.accessToken;
    
    if (!token) {
      throw new Error('No access token available');
    }
    
    console.log('Fetching complete portfolio...');
    
    // Fetch both holdings and positions in parallel
    const [holdingsResult, positionsResult] = await Promise.all([
      fetchHoldings(token),
      fetchPositions(token)
    ]);
    
    if (holdingsResult.success && positionsResult.success) {
      return {
        success: true,
        holdings: holdingsResult.holdings,
        positions: positionsResult.positions
      };
    } else {
      const errors = [
        !holdingsResult.success ? holdingsResult.error : null,
        !positionsResult.success ? positionsResult.error : null
      ].filter(Boolean);
      
      throw new Error(`Portfolio fetch failed: ${errors.join(', ')}`);
    }
  } catch (error) {
    console.error('Error fetching portfolio:', error);
    return { success: false, error: error.message || 'Failed to fetch portfolio' };
  }
};

// Save portfolio data to Supabase
export const savePortfolioToSupabase = async (
  userId: string, 
  holdings: KiteHolding[], 
  positions: { net: KitePosition[], day: KitePosition[] }
): Promise<any> => {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Prepare holdings data for database
    const holdingsData = holdings.map(holding => ({
      user_id: userId,
      instrument_token: holding.instrument_token,
      tradingsymbol: holding.tradingsymbol,
      exchange: holding.exchange,
      isin: holding.isin,
      product: holding.product,
      quantity: holding.quantity,
      average_price: holding.average_price,
      last_price: holding.last_price,
      close_price: holding.close_price,
      pnl: holding.pnl,
      day_change: holding.day_change,
      day_change_percentage: holding.day_change_percentage,
      last_updated: new Date().toISOString()
    }));
    
    // Prepare positions data for database
    const positionsData = positions.net.map(position => ({
      user_id: userId,
      instrument_token: position.instrument_token,
      tradingsymbol: position.tradingsymbol,
      exchange: position.exchange,
      product: position.product,
      quantity: position.quantity,
      average_price: position.average_price,
      last_price: position.last_price,
      pnl: position.pnl,
      value: position.value,
      last_updated: new Date().toISOString()
    }));
    
    // Save to database
    const { error: holdingsError } = await supabase
      .from('kite_holdings')
      .upsert(holdingsData, { onConflict: 'user_id,instrument_token' });
    
    if (holdingsError) {
      console.error('Error saving holdings:', holdingsError);
      throw holdingsError;
    }
    
    const { error: positionsError } = await supabase
      .from('kite_positions')
      .upsert(positionsData, { onConflict: 'user_id,instrument_token' });
    
    if (positionsError) {
      console.error('Error saving positions:', positionsError);
      throw positionsError;
    }
    
    console.log('Portfolio data saved successfully');
    return { success: true };
  } catch (error) {
    console.error('Error saving portfolio to database:', error);
    return { success: false, error: error.message || 'Failed to save portfolio' };
  }
};

// Convert holdings to investment format for profile setup
export const convertHoldingsToInvestments = (holdings: KiteHolding[]): any[] => {
  return holdings.map(holding => ({
    id: `kite_${holding.instrument_token}`,
    category: 'stocks',
    name: holding.tradingsymbol,
    value: (holding.quantity * holding.last_price).toString(),
    purchaseDate: holding.authorised_date?.split(' ')[0] || new Date().toISOString().split('T')[0],
    purpose: `Investment via Zerodha (${holding.exchange})`
  }));
};

// Clear authentication data
export const clearAuthData = (): void => {
  localStorage.removeItem('kite_access_token');
  localStorage.removeItem('kite_user_data');
  localStorage.removeItem('kite_auth_error');
  
  authState = {
    accessToken: null,
    userData: null,
    isAuthenticated: false
  };
  
  console.log('Kite authentication data cleared');
};

// Get last auth error
export const getLastAuthError = (): string | null => {
  return localStorage.getItem('kite_auth_error');
};

// Clear last auth error
export const clearLastAuthError = (): void => {
  localStorage.removeItem('kite_auth_error');
};

// Initialize auth state on import
initializeAuthState();

// Legacy function for holdings authorization (not needed in Connect API)
export const authorizeHoldings = async (holdings: any[] = [], callback?: any): Promise<boolean> => {
  console.warn('authorizeHoldings is deprecated in Connect API. Portfolio data is available directly after authentication.');
  
  if (callback) {
    callback({ 
      success: true, 
      message: 'Holdings authorization not required with Connect API' 
    });
  }
  
  return true;
};

// Backward compatibility exports (aliases for old function names)
export const extractKiteRequestToken = extractRequestToken;
export const exchangeKiteToken = exchangeToken;
export const fetchKiteHoldings = fetchHoldings;
export const fetchKitePositions = fetchPositions;
export const fetchKitePortfolio = fetchPortfolio;
export const convertKiteHoldingsToInvestments = convertHoldingsToInvestments;